// ---------------------------------------------------------------------------
// AITalk (Firebase) — ยูทิลิตี้กลาง + ชั้นข้อมูล (พอร์ตจาก gs/Core.gs)
//  โครงข้อมูล (RTDB) — ตารางแบนราบ: /<table>/<id> ; ตารางที่แยกตามเจ้าของ/ห้อง:
//    /chats/<uid>/<chatId>  /messages/<chatId>/<msgId>  /coin_tx/<uid>/<id>  /notifications/<uid>/<id>
//    /checkins/<uid>/<date>  /likes/<uid>/<type>|<targetId>  /follows/<uid>/<targetUid>  /memories/<chatId>
//  ดัชนีช่วย: /usernames /referral_codes /share_codes /activity/<day>/... /locks
// ---------------------------------------------------------------------------
import { fb, fk, FbError } from "../fbClient";

export { fb, fk };

export const TZ_MS = 7 * 3600 * 1000; // เวลาไทย

export const err = (code, message) => new FbError(code, message || code);

// ---- แปลงค่า ------------------------------------------------------------------
export const nowIso = () => new Date().toISOString();

/** วันที่ตามเวลาไทย YYYY-MM-DD (offsetDays = เลื่อนวัน) */
export const dayKey = (offsetDays, atMs) => {
  const t = (atMs === undefined ? Date.now() : atMs) + TZ_MS + (offsetDays || 0) * 86400000;
  return new Date(t).toISOString().slice(0, 10);
};
export const monthKey = (atMs) => dayKey(0, atMs).slice(0, 7);

let idSeq = 0;
/** id เรียงตามเวลา (ตัวอักษรคงที่ -> เรียงตาม key ได้) */
export const newId = (prefix) => {
  idSeq = (idSeq + 1) % 1296;
  let t = Date.now().toString(36);
  while (t.length < 9) t = "0" + t;
  let s = idSeq.toString(36);
  while (s.length < 2) s = "0" + s;
  const rnd = Math.floor(Math.random() * 1679616).toString(36).padStart(4, "0");
  return prefix + t + s + rnd;
};

export const str = (v, max, name, required) => {
  const s = v === undefined || v === null ? "" : String(v).trim();
  if (required && !s) throw err("bad_request", "กรุณาระบุ " + (name || "ค่า"));
  if (max && s.length > max) throw err("bad_request", (name || "ค่า") + "ยาวเกิน " + max + " ตัวอักษร");
  return s;
};
export const num = (v, def) => {
  const d = def === undefined ? 0 : def;
  if (v === null || v === undefined || v === "") return d;
  const n = Number(v);
  return isFinite(n) ? n : d;
};
export const bool = (v) => v === true || v === "true" || v === 1 || v === "1";
export const limitOf = (v, def, max) => {
  let n = parseInt(v, 10);
  if (!(n > 0)) n = def;
  return Math.min(n, max || 50);
};
export const parseJson = (v, def) => {
  if (v === undefined || v === null || v === "") return def;
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return def;
  }
};
export const asArray = (v, max) => {
  let a = parseJson(v, []);
  if (typeof a === "string") a = a.split(",");
  if (!Array.isArray(a)) a = a && typeof a === "object" ? Object.values(a) : [];
  a = a.map((x) => (typeof x === "string" ? x.trim() : x)).filter((x) => x !== "" && x !== null && x !== undefined);
  return max ? a.slice(0, max) : a;
};
export const pick = (obj, keys) => {
  const o = {};
  keys.forEach((k) => {
    if (obj[k] !== undefined) o[k] = obj[k];
  });
  return o;
};
export const replaceVars = (text, userName, charName) =>
  String(text || "").replace(/\{\{\s*user\s*\}\}/gi, userName || "ผู้ใช้").replace(/\{\{\s*char\s*\}\}/gi, charName || "ตัวละคร");

/** ตัด null/undefined ออกจากออบเจ็กต์ (RTDB ไม่เก็บ null) */
export const clean = (o) => {
  const out = {};
  Object.keys(o || {}).forEach((k) => {
    if (o[k] !== null && o[k] !== undefined) out[k] = o[k];
  });
  return out;
};

// ---- rate limit (ในเครื่อง — กันกดรัว ไม่ใช่ความปลอดภัย) --------------------------------
const rl = new Map();
export const rateLimit = (key, max, sec) => {
  const now = Date.now();
  const e = rl.get(key);
  if (!e || e.until < now) {
    rl.set(key, { n: 1, until: now + sec * 1000 });
    return;
  }
  if (e.n >= max) throw err("rate_limited", "เรียกถี่เกินไป กรุณารอสักครู่");
  e.n++;
};
export const resetRateLimits = () => rl.clear();

// ---- lock แบบ flag ใน RTDB (กันส่งซ้อน/ตรวจเงินซ้ำ) -------------------------------------
export async function acquireFlag(name, sec) {
  const r = await fb.transaction(`locks/${name.split("/").map(fk).join("/")}`, (cur) => (cur && cur > Date.now() ? undefined : Date.now() + (sec || 60) * 1000));
  return r.committed;
}
export const releaseFlag = (name) => fb.remove(`locks/${name.split("/").map(fk).join("/")}`).catch(() => {});

// ---- ตาราง/แถว ------------------------------------------------------------------------
/** แถวจากโหนด ถ้าไม่มีฟิลด์ id ให้ใช้ key */
const withId = (key, v) => (v && typeof v === "object" ? (v.id === undefined ? { id: key, ...v } : v) : v);

export const getRow = async (path) => {
  const v = await fb.get(path);
  if (!v || typeof v !== "object") return null;
  const key = path.split("/").pop();
  return withId(v.id !== undefined ? v.id : decodeURIComponent(key), v);
};

export const rowsOf = (obj) => Object.entries(obj || {}).map(([k, v]) => withId(decodeURIComponent(k), v)).filter((v) => v && typeof v === "object");

/** แถวใหม่ : เติม id/createdAt/updatedAt */
export const prepareInsert = (prefix, obj) => {
  const now = nowIso();
  const o = clean(obj);
  if (!o.id) o.id = newId(prefix);
  o.createdAt = o.createdAt || now;
  o.updatedAt = now;
  return o;
};

/** เพิ่มแถวที่ path (เขียนทับ) */
export async function putRow(path, prefix, obj) {
  const row = prepareInsert(prefix, obj);
  await fb.put(path, row);
  return row;
}

/** เพิ่มแถวใหม่ใต้ base (สร้าง id เรียงตามเวลา) -> แถวที่เขียน */
export async function putNew(base, prefix, obj) {
  const id = obj.id || newId(prefix);
  return putRow(`${base}/${fk(id)}`, prefix, { ...obj, id });
}

/** แก้บางฟิลด์แบบ atomic (transaction) — ไม่พบ = not_found ; patch เป็นออบเจ็กต์หรือฟังก์ชัน (แถวปัจจุบัน -> patch) ; ค่า null/undefined = ลบฟิลด์ */
export async function updateRow(path, patchOrFn, what) {
  let result = null;
  const r = await fb.transaction(path, (cur) => {
    if (!cur || typeof cur !== "object") throw err("not_found", "ไม่พบข้อมูล" + (what ? " " + what : ""));
    const patch = typeof patchOrFn === "function" ? patchOrFn(cur) : patchOrFn;
    const next = { ...cur };
    Object.keys(patch).forEach((k) => {
      if (k === "id" || k === "createdAt") return;
      if (patch[k] === null || patch[k] === undefined) delete next[k];
      else next[k] = patch[k];
    });
    next.updatedAt = nowIso();
    result = next;
    return next;
  });
  return r.committed ? result : null;
}

/** บวก/ลบตัวเลขในฟิลด์ (ไม่ติดลบ) — ไม่สร้างแถวถ้าแถวไม่มีอยู่ */
export async function incr(rowPath, field, by) {
  let out = null;
  if ((await fb.get(`${rowPath}/id`)) === null) return null; // แถวต้องมีอยู่ (ทุกแถวมีฟิลด์ id)
  await fb.transaction(`${rowPath}/${field}`, (cur) => {
    out = Math.max(0, num(cur) + by);
    return out;
  });
  return out;
}

/** นับสถิติรายวัน (อันดับ/แดชบอร์ด) — ล้มเหลวไม่กระทบงานหลัก */
export async function bump(metric, id, by = 1) {
  try {
    const p = id ? `activity/${dayKey()}/${metric}/${fk(id)}` : `activity/${dayKey()}/${metric}`;
    await fb.transaction(p, (cur) => num(cur) + by);
  } catch (e) {
    /* ignore */
  }
}

/**
 * แบ่งหน้าจากลิสต์ในหน่วยความจำ (ใหม่->เก่า) ตามคีย์เรียง sort ("id" | "createdAt")
 * คืน {items, nextCursor} ; cursor = ค่า sort ของแถวสุดท้ายในหน้าก่อน
 */
export function pageRows(rows, { limit, cursor, filter, sort = "id" } = {}) {
  let list = rows.slice().sort((a, b) => (String(b[sort]) < String(a[sort]) ? -1 : String(b[sort]) > String(a[sort]) ? 1 : 0));
  if (cursor) list = list.filter((r) => String(r[sort]) < String(cursor));
  if (filter) list = list.filter(filter);
  const items = list.slice(0, limit);
  return { items, nextCursor: list.length > limit ? String(items[items.length - 1][sort]) : null };
}

/** แบ่งหน้าจากโหนดที่ key เรียงตามเวลา (messages/coin_tx/notifications) ใหม่->เก่า, อ่านจากท้ายด้วย orderBy $key + limitToLast */
export async function pageByKey(path, { limit, cursor, filter } = {}) {
  const items = [];
  let end = cursor || null;
  const fetchN = Math.max(limit * 2, 20);
  for (let guard = 0; guard < 25 && items.length <= limit; guard++) {
    const q = { orderBy: "$key", limitToLast: fetchN + (end ? 1 : 0) };
    if (end) q.endAt = end;
    let rows = await fb.entries(path, q);
    const upper = end;
    if (upper) rows = rows.filter(([k]) => decodeURIComponent(k) < upper);
    if (!rows.length) break;
    for (let i = rows.length - 1; i >= 0 && items.length <= limit; i--) {
      const row = withId(decodeURIComponent(rows[i][0]), rows[i][1]);
      if (filter && !filter(row)) continue;
      items.push(row);
    }
    end = decodeURIComponent(rows[0][0]);
    if (rows.length < fetchN) break;
  }
  const hasMore = items.length > limit;
  const page = hasMore ? items.slice(0, limit) : items;
  return { items: page, nextCursor: hasMore ? page[page.length - 1].id : null };
}

// ---- ตารางตั้งค่า (อ่านทั้งโหนด + cache ในหน่วยความจำ) ---------------------------------------
export const CACHED_TABLES = ["character_tags", "packages", "vip_tiers", "ai_models", "gifts", "settings"];
const TTL = 5 * 60 * 1000;
const tableCache = new Map();

export async function readAll(table) {
  const hit = tableCache.get(table);
  if (hit && Date.now() - hit.t < TTL) return hit.rows;
  const rows = rowsOf(await fb.get(table));
  tableCache.set(table, { t: Date.now(), rows });
  return rows;
}
export const readAllRaw = async (table) => rowsOf(await fb.get(table));
export const touchCache = (table) => tableCache.delete(table);
export const clearAllCaches = () => {
  tableCache.clear();
  memo.clear();
};

/** cache ผลคำนวณสั้น ๆ (การ์ดตัวละคร/อันดับ) */
const memo = new Map();
export async function memoize(key, ttlMs, fn) {
  const hit = memo.get(key);
  if (hit && Date.now() - hit.t < ttlMs) return hit.v;
  const v = await fn();
  memo.set(key, { t: Date.now(), v });
  return v;
}
export const dropMemo = (prefix) => {
  Array.from(memo.keys()).forEach((k) => {
    if (k.startsWith(prefix)) memo.delete(k);
  });
};

// ---- Settings --------------------------------------------------------------------------
export const DEFAULT_SETTINGS = {
  signup_coins: 50, checkin_coins: 30, checkin_bonus_every: 10, checkin_bonus_coins: 30, checkin_bonus_coins_pro: 60,
  referral_inviter_coins: 50, referral_invitee_coins: 50, referral_max_invites: 5, gift_creator_share: 0.3,
  char_approval_required: false, max_chars_per_user: 50, context_messages: 16, max_message_len: 6000,
  topup_require_init: true, ai_daily_free_limit: 20, announcement: "",
  // ราคาเติมคอยน์แบบกำหนดเอง : คอยน์ = บาท x coin_per_baht + โบนัสตามขั้น ("ยอดขั้นต่ำ:%,...")
  coin_per_baht: 5, custom_topup_min: 300, custom_topup_max: 50000, coin_bonus_tiers: "500:5,1000:10,2000:15,5000:25",
  // สิทธิ์สร้างตัวละคร : ราคาต่อตัว + ส่วนลดตามจำนวน ("จำนวนขั้นต่ำ:%,...") ; ปิด create_credit_required = เผยแพร่ฟรีเหมือนเดิม
  create_credit_price: 300, create_credit_max: 100, create_credit_tiers: "1:0,5:5,10:10,30:12,50:15,100:20", create_credit_required: true,
};
export const PUBLIC_SETTINGS = ["announcement", "checkin_coins", "checkin_bonus_every", "checkin_bonus_coins", "max_message_len",
  "referral_inviter_coins", "referral_invitee_coins", "referral_max_invites"];

export async function settings() {
  const s = { ...DEFAULT_SETTINGS };
  (await readAll("settings")).forEach((r) => {
    const d = DEFAULT_SETTINGS[r.id];
    let v = r.value;
    if (v === null || v === undefined || v === "") {
      if (typeof d === "string") s[r.id] = "";
      return;
    }
    if (typeof d === "number") v = num(v, d);
    else if (typeof d === "boolean") v = bool(v);
    s[r.id] = v;
  });
  return s;
}
export const setting = async (key) => (await settings())[key];
